
import Stage from '@util/stage'
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@/utils/types';
import { ZoneSuggestions } from '@/generated-clients/myTrips';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import { zoneIcons } from '@util/settingIcons'
import { ZoneWithRules } from '@/models/SettingsModels'

const SettingsModule = namespace('Settings');

@Component({
  name: 'ZonesOverview',
  components: {
    SettingsPanel,
    AppListItem,
    InfoBoxWarning,
    AppContent,
    AppPage,
    AppHeader,
    SettingsTitleBar
  }
})
export default class ZonesOverview extends Vue {
  @SettingsModule.Action('createZone')
  private createZone;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Action('fetchZoneSuggestions')
  private fetchZoneSuggestions;

  @SettingsModule.Getter('getZones')
  private zones: ZoneWithRules[];

  @SettingsModule.Getter('getZoneSuggestions')
  private zoneSuggestions: ZoneSuggestions;

  @SettingsModule.Getter('getZonesStatus')
  private zonesStatus: LoadState;

  @SettingsModule.Getter('getZoneSuggestionsStatus')
  private zoneSuggestionsStatus: LoadState;

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Getter('getTagsStatus')
  private tagsStatus: LoadState;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getRulesStatus')
  private rulesStatus: LoadState;

  @SettingsModule.Getter('getStage')
  private stage: Stage;

  private disabled = false;

  private created() {
    this.fetchZones();
    this.fetchZoneSuggestions();
    this.fetchPilots();
    this.fetchTags();
    this.fetchRules()
  }

  get backButton() {
    return { text: 'Zurück zu Anpassen', location: { name: 'mySettings' } };
  }

  get addButton() {
    return { text: 'Neues Ziel anlegen', location: { name: 'zonesAdd' } };
  }

  get createItem(): ListItem {
    return { title: 'Neues Ziel erstellen', tagIcon: { value: 'tag_plus', color: '#FFDC00', fill: 'black' }, location: { name: 'zonesAdd' } };
  }

  get zoneItems(): ListItem[] {
    if (this.isLoading) {
      return [];
    }

    return this.zones.map(z => ({ id: z.id, title: z.name, ...zoneIcons(z), location: { name: 'zoneEdit', params: { id: z.id.toString() } }, hoverText: 'Ziel bearbeiten' }));
  }

  get suggestionsHeaderText() {
    return 'Diese Vorschläge kommen zustande, wenn Sie innerhalb von 14 Tagen mindestens 4 mal in einem gewissen Umkreis eine Fahrt beendet haben.';
  }

  get zoneSuggestionsItems(): ListItem[] {
    if (this.zoneSuggestionsStatus != LoadState.Success) {
      return [];
    }

    return [
      ...this.zoneSuggestions.house.map(hs => ({ type: 'house', title: hs.name, tagIcon: { value: 'tag_default_icon', color: '#abb4bd', fill: 'black' } })),
      ...this.zoneSuggestions.work.map(ws => ({ type: 'work', title: ws.name, tagIcon: { value: 'tag_default_icon', color: '#abb4bd', fill: 'black' } })),
    ];
  }

  private async addSuggestion(type: string, index: number) {
    const zone = this.zoneSuggestions[type][index];

    this.disabled = true;

    try {
      await this.createZone({ zone });
    } finally {
      this.disabled = false;
    }
  }

  get errorLoading() {
    return this.zonesStatus === LoadState.Error;
  }

  get isLoading() {
    return this.zonesStatus === LoadState.Loading
      || this.zoneSuggestionsStatus === LoadState.Loading
      || this.tagsStatus === LoadState.Loading
      || this.pilotsStatus === LoadState.Loading
      || this.rulesStatus === LoadState.Loading;
  }

  get hasAnySynchronizedZones() {
    return this.zones.some(zone => zone.synchronizedWithGisFavorite);
  }

  get gisFavoritesUrl() {
    switch (this.stage) {
      case Stage.INT: return 'https://webint.oeamtc.at/routenplaner/poi/favoriten';
      case Stage.TEST: return 'https://beta.oeamtc.at/routenplaner/poi/favoriten';
      case Stage.PROD: return 'https://oeamtc.at/routenplaner/poi/favoriten';
    }
  }
}
